import { useEffect, useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CircleDot, FolderOpen } from 'lucide-react';
import { useAppStore } from '../../store';
import type { Task } from '../../types';
import { canScheduleTimelineItem } from '../../lib/timeline';
import { timelineTargetHighlightClassName } from '../shared/highlightStyles';

const priorityColors: Record<string, string> = {
  p1: 'bg-red-400',
  p2: 'bg-amber-400',
  p3: 'bg-blue-400',
};

interface PlannerSidebarItemProps {
  task: Task;
  isSubtask?: boolean;
}

export function PlannerSidebarItem({ task, isSubtask }: PlannerSidebarItemProps) {
  const elRef = useRef<HTMLDivElement>(null);
  const didDrag = useRef(false);
  const canSchedule = canScheduleTimelineItem(task);
  const idleClassName = canSchedule
    ? 'transition-[background-color,box-shadow] duration-150 hover:bg-bg-raised/60 active:bg-bg-raised'
    : 'transition-[box-shadow] duration-150';
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `sidebar-${task.id}`,
    data: {
      type: canSchedule ? 'sidebar-task' : 'sidebar-reference',
      taskId: task.id,
      title: task.title,
      priority: task.priority,
      itemType: task.itemType,
      parentId: task.parentId,
    },
    disabled: !canSchedule,
  });

  const highlightedTaskId = useAppStore((s) => s.highlightedTaskId);
  const isHighlighted = highlightedTaskId === task.id;

  useEffect(() => {
    if (isDragging) didDrag.current = true;
  }, [isDragging]);

  useEffect(() => {
    if (isHighlighted && elRef.current) {
      elRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isHighlighted]);

  const setRefs = (el: HTMLDivElement | null) => {
    elRef.current = el;
    setNodeRef(el);
  };

  const handleClick = () => {
    if (didDrag.current) { didDrag.current = false; return; }
    useAppStore.getState().setSelectedTaskId(task.id);
  };

  return (
    <div
      ref={setRefs}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      className={`flex items-center gap-2 py-1.5 px-3 rounded-md select-none ${canSchedule ? 'cursor-grab' : 'cursor-default'} ${
        isSubtask ? 'ml-3' : ''
      } ${isHighlighted ? timelineTargetHighlightClassName : idleClassName}`}
      style={{
        opacity: isDragging ? 0.3 : 1,
        touchAction: 'none',
      }}
    >
      {isSubtask || task.parentId ? (
        <span
          className={`w-2 h-2 rounded-full shrink-0 ${priorityColors[task.priority] ?? 'bg-blue-400'}`}
        />
      ) : task.itemType === 'project' ? (
        <FolderOpen size={12} className="text-accent shrink-0" />
      ) : (
        <CircleDot size={12} className="text-accent shrink-0" />
      )}
      <span
        className={`text-[12px] truncate leading-tight ${
          isSubtask ? 'text-text-muted' : 'text-text-primary font-medium'
        }`}
      >
        {task.title}
      </span>
    </div>
  );
}
